import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@material-ui/core';
import create, { UseStore } from 'zustand';

import { fetchRequest } from './';
import { Search, Text } from './controls';
import { NdcInformation, Procedure } from './domain';
import { FormApi, formApi } from './form';
import { worksheetMessage, WorksheetResult } from './worksheet-service';

type NdcResult = {
  ndcNumber: string;
  description: string;
  measure: string;
};

type NdcListResult = WorksheetResult<NdcResult[]>;
type NdcArgs = {
  code: string;
  locationCode: string;
  departmentCode: string;
  worksheetType: string;
};

export const getNdcListRequest = fetchRequest<any, NdcListResult>({
  method: 'post',
  body: (args: NdcArgs) => worksheetMessage(args, 'getndclist'),
});

type NdcProps = {
  cpt?: Procedure;
  info?: NdcResult[];
  onSubmit?: (cpt: Procedure) => void;
};

type ValueLabel = { value: string; label: string };
type NdcFormValues = {
  ndc?: NdcResult;
  unit?: ValueLabel;
  quantity?: string;
};

type NdcActions = {
  open: boolean;
  edit: (cpt: Procedure, onSubmit?: (cpt: Procedure) => void) => void;
};

export type NdcApi = {
  actions: UseStore<NdcActions>;
  form: FormApi<NdcFormValues>;
  update(a: NdcProps): void;
};

const units: ValueLabel[] = [
  { value: 'GR', label: 'Grams' },
  { value: 'ME', label: 'Milligrams' },
  { value: 'ML', label: 'Milliliters' },
  { value: 'UN', label: 'Units' },
];

export function ndcApi(client: Omit<NdcArgs, 'code'>): NdcApi {
  let ctx: NdcProps = {};

  const initialValues: NdcFormValues = {
    ndc: null,
    unit: null,
    quantity: null,
  };
  const form: FormApi<NdcFormValues> = formApi<NdcFormValues>({
    values: initialValues,
    fields: {
      ndc: {
        required: true,
        label: 'NDC List',
        autoFocus: true,
        list: [],
        listLabel: (a: NdcResult) => a.description,
        onChange: (a: NdcResult) => {
          if (a && !unit.value.getState().value) {
            unit.setListValue((v: ValueLabel) => a.measure === v.value);
          }
        },
      },
      unit: {
        required: true,
        label: 'Unit of Measure',
        listLabel: (a: ValueLabel) => a.label,
        list: units,
      },
      quantity: {
        required: true,
        label: 'Quantity',
        onEnter: () => form.submit(),
      },
    },
    valid: (a) => !!a.ndc && !!a.unit && !!a.quantity?.length,
    onSubmit: (a) => {
      const { cpt, onSubmit } = ctx;

      if (cpt) {
        cpt.ndcInformation = {
          ndcDescription: a.ndc.description,
          ndcNumber: a.ndc.ndcNumber,
          ndcMeasure: a.unit.value,
          ndcQuantity: a.quantity,
        };
      }

      if (onSubmit) {
        onSubmit(cpt);
      }
      actions.setState({ open: false });
    },
  });

  const { ndc, unit, quantity } = form.controls;

  function update(a: NdcProps): void {
    ctx = { ...ctx, ...a };

    const info = a?.cpt?.ndcInformation;
    if (info) {
      quantity.value.setState({ value: info.ndcQuantity });
    }
    if (a?.info?.length) {
      ndc.list.setState({ value: a.info });
    }
  }

  function setCptInfo(info: NdcInformation) {
    console.log('setCptInfo', info);
    if (info) {
      ndc.setListValue((a: NdcResult) => a.ndcNumber === info.ndcNumber);
      unit.setListValue((a: ValueLabel) => a.value === info.ndcMeasure);
      quantity.value.setState({ value: info.ndcQuantity });
    } else {
      form.setValues(
        {
          ndc: null,
          unit: null,
          quantity: null,
        },
        true
      );
    }
  }

  const actions = create<NdcActions>((set) => ({
    open: false,
    edit: async (cpt: Procedure, onSubmit?: (cpt: Procedure) => void) => {
      ctx.onSubmit = onSubmit;

      const response = await getNdcListRequest({
        ...client,
        ...{ code: cpt.cptCode },
      });

      if (response?.result?.length) {
        ndc.list.setState({ value: response.result });
      } else {
        onSubmit && onSubmit(cpt);
        return;
      }
      ctx.cpt = cpt;
      setCptInfo(cpt?.ndcInformation);
      //TODO init search
      set({ open: true });
    },
  }));

  return {
    actions,
    form,
    update,
  };
}

export function NdcDialog(api: NdcApi) {
  const { form, actions } = api;
  const open = actions((s) => s.open);

  const { ndc, unit, quantity } = form.controls;

  return (
    <Dialog open={open} maxWidth={false}>
      <DialogTitle>NDC Information</DialogTitle>
      <DialogContent style={{ minWidth: 580 }}>
        <Grid container direction="column" spacing={2} style={{ width: '100%' }}>
          <Grid item style={{ width: '100%' }}>
            <Search {...ndc} />
          </Grid>
          <Grid item container direction="row" spacing={2} style={{ width: '103%', flexWrap: 'nowrap' }}>
            <Grid item style={{ width: '100%' }}>
              <Search {...unit} />
            </Grid>
            <Grid item style={{ width: '100%' }}>
              <Text {...quantity} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <br />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => actions.setState({ open: false })} color="primary" variant="outlined">
          {' '}
          Cancel{' '}
        </Button>
        <Button onClick={() => form.submit()} color="primary" variant="contained">
          {' '}
          Submit{' '}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
